import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { userService } from '../../services/api'
import { User } from '../../models/User.model'

const initialState = {
  users: {},
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  },
  filters: {
    role: 'all',
    search: '',
    status: 'all'
  }
}

export const fetchUserById = createAsyncThunk(
  'users/fetchUserById',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.getById(userId)
      
      // Verificar se a resposta é válida
      if (!response) {
        return rejectWithValue('Resposta inválida da API')
      }
      
      // Extrair dados do usuário de diferentes estruturas possíveis
      let user = null
      
      if (response.data) {
        // Estrutura 1: response.data.obj.user
        if (response.data.obj && response.data.obj.user) {
          user = response.data.obj.user
        } else if (response.data.user) {
          user = response.data.user
        } else if (response.data.id) {
          user = response.data
        }
      }
      
      if (!user) {
        return rejectWithValue('Usuário não encontrado')
      }
      
      return user
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user')
    }
  }
)

export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await userService.getAll(filters)      
      // Verificar se a resposta é válida
      if (!response) {
        return rejectWithValue('Resposta inválida da API')
      }
      
      // Verificar diferentes estruturas de resposta possíveis
      let users = []
      let pagination = {}
      
      if (response.data) {
        // Estrutura 1: response.data.users
        if (response.data.users && Array.isArray(response.data.users)) {
          users = response.data.users
          pagination = response.data.pagination || {}
        } else if (Array.isArray(response.data)) {
          users = response.data
          pagination = { currentPage: 1, totalPages: 1, totalItems: response.data.length }
        } else if (response.data.obj && response.data.obj.users) {
          users = response.data.obj.users
          pagination = response.data.obj.pagination || {}
        } else if (response.data.obj && Array.isArray(response.data.obj)) {
          users = response.data.obj
          pagination = { currentPage: 1, totalPages: 1, totalItems: response.data.obj.length }
        } else if (response.data.data && Array.isArray(response.data.data)) {
          users = response.data.data
          pagination = response.data.pagination || {}
        } else {
          // Tentar usar response.data como array se for um array
          if (Array.isArray(response.data)) {
            users = response.data
            pagination = { currentPage: 1, totalPages: 1, totalItems: response.data.length }
          }
        }
      }
      const result = {
        users,
        pagination
      }
      
      return result
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch users')
    }
  }
)

export const createUser = createAsyncThunk(
  'users/createUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await userService.create(userData)
      // Extrair dados do usuário de diferentes estruturas possíveis
      const user = response.data?.user || response.data?.obj?.user || response.data
      return user
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create user')
    }
  }
)

export const updateUser = createAsyncThunk(
  'users/updateUser',
  async ({ userId, userData }, { rejectWithValue }) => {
    try {
      const response = await userService.update(userId, userData)
      // Extrair dados do usuário de diferentes estruturas possíveis
      const user = response.data?.user || response.data?.obj?.user || response.data
      return user
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update user')
    }
  }
)

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (userId, { rejectWithValue }) => {
    try {
      await userService.delete(userId)
      return userId
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete user')
    }
  }
)

export const updateUserRoles = createAsyncThunk(
  'users/updateUserRoles',
  async ({ userId, roles }, { rejectWithValue }) => {
    try {
      const response = await userService.updateRoles(userId, roles)
      // Extrair dados do usuário de diferentes estruturas possíveis
      const user = response.data?.user || response.data?.obj?.user || response.data
      return user
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update user roles')
    }
  }
)

export const verifyUser = createAsyncThunk(
  'users/verifyUser',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.verify(userId)
      // Extrair dados do usuário de diferentes estruturas possíveis
      const user = response.data?.user || response.data?.obj?.user || response.data
      return user
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to verify user')
    }
  }
)

export const updateUserCapabilities = createAsyncThunk(
  'users/updateUserCapabilities',
  async ({ userId, capabilities }, { rejectWithValue }) => {
    try {
      const response = await userService.updateCapabilities(userId, capabilities)
      return { userId, capabilities: response.data }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao atualizar capabilities')
    }
  }
)

export const updateUserProfile = createAsyncThunk(
  'users/updateUserProfile',
  async ({ userId, profileData }, { rejectWithValue }) => {
    try {
      const response = await userService.updateProfile(userId, profileData)
      // Extrair dados do usuário de diferentes estruturas possíveis
      const user = response.data?.user || response.data?.obj?.user || response.data
      return user
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update user profile')
    }
  }
)

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    setLoading: (state, action) => {
      state.loading = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload && action.payload.id) {
          state.users[action.payload.id] = action.payload
        }
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false
        state.users = {}
        
        const users = action.payload?.users || []
        
        if (Array.isArray(users)) {
          users.forEach(user => {
            if (user && user.id) {
              state.users[user.id] = user
            }
          })
        }
        
        state.pagination = action.payload?.pagination || state.pagination
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      .addCase(createUser.fulfilled, (state, action) => {
        state.users[action.payload.id] = action.payload
      })
      
      .addCase(updateUser.fulfilled, (state, action) => {
        state.users[action.payload.id] = action.payload
      })
      
      .addCase(deleteUser.fulfilled, (state, action) => {
        delete state.users[action.payload]
      })
      
      .addCase(updateUserRoles.fulfilled, (state, action) => {
        state.users[action.payload.id] = action.payload
      })
      
      .addCase(verifyUser.fulfilled, (state, action) => {
        state.users[action.payload.id] = action.payload
      })
      
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.users[action.payload.id] = action.payload
      })
      
      .addCase(updateUserCapabilities.fulfilled, (state, action) => {
        const { userId, capabilities } = action.payload
        if (state.users[userId]) {
          state.users[userId] = {
            ...state.users[userId],
            dataCapabilities: capabilities.dataCapabilities || state.users[userId].dataCapabilities,
            binding: capabilities.binding || state.users[userId].binding
          }
        }
      })
  }
})

export const { clearError, setFilters, setLoading } = usersSlice.actions

export const selectUsers = (state) => state.users.users
export const selectUserById = (id) => (state) => state.users.users[id]
export const selectUsersLoading = (state) => state.users.loading
export const selectUsersError = (state) => state.users.error
export const selectUsersPagination = (state) => state.users.pagination
export const selectUsersFilters = (state) => state.users.filters

export default usersSlice.reducer